import "./AssignOwnerModal.css";

import {
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  ModalIsland,
  Typography,
} from "@saviynt/design-system";
import { useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import {
  SelectedRowsProvider,
  useSelectedRows,
} from "../../context/SelectedRowsContext";
import {
  AssignOwnerFilterProvider,
  useAssignOwnerFilter,
} from "../AssignOwnerFilter/AssignOwnerFilterContext";
import AssignOwnerFilterControl from "../AssignOwnerFilter/AssignOwnerFilterControl";
import AssignOwnerFilterOptions from "../AssignOwnerFilter/AssignOwnerFilterOptions";
import AssignOwnerTable from "../AssignOwnerTable/AssignOwnerTable";
import SelectedOwners from "../SelectedOwners/SelectedOwners";
import {
  useAssignOwnerToBulkAssets,
  useAssignOwnerToAllAssets,
  useAssignOwnerToSigleAsset,
  usePreviouslyAssignedOwners,
} from "../../../../../../../../hooks/useAssignOwners";

export interface InventoryTablePreference {
  sortBy?: string | null;
  sortOrder?: string | null;
  filters: {
    name: string;
    value: string;
    operator: string;
  }[];
  nextPageToken?: string | null;
}

interface AssignOwnerModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isBulkAssign?: boolean;
  selectAllItems?: boolean;
  assetId?: string;
  assetIds?: string[];
  requestPayload?: InventoryTablePreference;
  clearSelection?: () => void;
}

function AssignOwnerModalContent({
  isOpen,
  setIsOpen,
  assetId,
  assetIds,
  isBulkAssign = false,
  selectAllItems = false,
  requestPayload,
  clearSelection,
}: AssignOwnerModalProps) {
  const intl = useIntl();
  const { selectedRows } = useSelectedRows();
  const { searchValue, setSearchValue } = useAssignOwnerFilter();
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const sectionRef = useRef(null);

  const { mutate: assignOwnerToBulkAssets } =
    useAssignOwnerToBulkAssets(clearSelection);
  const { mutate: assignOwnerToAllAssets } =
    useAssignOwnerToAllAssets(clearSelection);
  const { mutate: assignOwnerToSingleAsset } = useAssignOwnerToSigleAsset(
    assetId ?? "",
    clearSelection,
  );

  const closeModal = useCallback(() => setIsOpen(false), [setIsOpen]);

  const HeaderComp = useMemo(
    () => (
      <Box>
        <header className="NHI-Assign-Owner-Modal-headerWrapper">
          <div className="NHI-Assign-Owner-Modal-header">
            <div className="NHI-Assign-Owner-Modal-titleContainer">
              <Typography
                kind="h2"
                desktop="h2-d"
                mobile="h2-d"
                color="--color-foreground-neutral-bold"
                className="NHI-Assign-Owner-Modal-title"
              >
                {intl.formatMessage({
                  defaultMessage: "Assign Owner",
                  id: "qpmK5Y",
                  description: "Button text to assign owner",
                })}
              </Typography>
            </div>
          </div>
          <div className="NHI-Assign-Owner-Modal-closeBtn">
            <ButtonIcon
              size="medium"
              kind="ghost"
              icon={<Icon kind="Close" size="smallMedium" />}
              onClick={closeModal}
              ariaLabel={intl.formatMessage({
                defaultMessage: "Modal Close Button",
                id: "Yrvf8V",
                description: "Button text to close modal",
              })}
              dataTestId="NHI-Assign-Owner-Modal-CloseButton"
            />
          </div>
        </header>
        <SelectedOwners />
      </Box>
    ),
    [intl, closeModal],
  );

  const handleAssignOwner = useCallback(() => {
    const userIds = selectedRows.map((row) => row.userkey);

    if (selectAllItems) {
      assignOwnerToAllAssets({
        userIds,
        assetFilters: requestPayload?.filters ?? [],
      });
    } else if (isBulkAssign) {
      const payload = { assetIds: assetIds ?? [], userIds };
      assignOwnerToBulkAssets(payload);
    } else {
      const payload = { assetId: assetId ?? "", userIds };
      assignOwnerToSingleAsset(payload);
    }
    closeModal();
  }, [
    selectedRows,
    selectAllItems,
    isBulkAssign,
    closeModal,
    assignOwnerToAllAssets,
    requestPayload?.filters,
    assetIds,
    assignOwnerToBulkAssets,
    assetId,
    assignOwnerToSingleAsset,
  ]);

  const getModalBodyContent = () => {
    return (
      <>
        <AssignOwnerFilterControl
          isFilterOpen={isFilterOpen}
          handleFilterClick={() => setIsFilterOpen(!isFilterOpen)}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />
        {isFilterOpen && <AssignOwnerFilterOptions />}
        <AssignOwnerTable />
      </>
    );
  };

  return (
    <ModalIsland
      onClose={closeModal}
      isOpen={isOpen}
      HeaderComp={HeaderComp}
      contentBody={getModalBodyContent()}
      sectionRef={sectionRef}
      FooterActionBarComp={
        <FooterActionBar size="large" className="gap-4">
          <Button kind="ghost" className="ml-auto" onClick={closeModal}>
            {intl.formatMessage({
              defaultMessage: "Cancel",
              description: "Button to cancel assign owner operation",
              id: "BNFjbc",
            })}
          </Button>
          <Button kind="filled" onClick={handleAssignOwner}>
            {intl.formatMessage({
              defaultMessage: "Assign Owner",
              description: "Button to confirm assign owner operation",
              id: "c1XO2p",
            })}
          </Button>
        </FooterActionBar>
      }
      className="NHI-Assign-Owner-Modal"
    />
  );
}

export default function AssignOwnerModal(props: AssignOwnerModalProps) {
  const { assetId, isBulkAssign = false } = props;
  const { data, isLoading } = usePreviouslyAssignedOwners(
    assetId ?? "",
    isBulkAssign,
  );

  const initialSelectedRows = useMemo(
    () => (!isLoading ? data?.value || [] : []),
    [isLoading, data?.value],
  );

  return (
    // @ts-expect-error: Need to verify type of initialSelectedRows
    <SelectedRowsProvider initialValue={initialSelectedRows}>
      <AssignOwnerFilterProvider>
        <AssignOwnerModalContent {...props} />
      </AssignOwnerFilterProvider>
    </SelectedRowsProvider>
  );
}
